import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { SITE_URL, absoluteUrl, generateBreadcrumbs } from '@/lib/seo';

export interface FAQ {
  question: string;
  answer: string;
}

export interface ServiceSchema {
  name: string;
  description: string;
  serviceType: string;
  priceRange: string;
  areaServed: string[];
}

export interface CaseStudySchema {
  name: string;
  description: string;
  datePublished: string;
  about: string;
  result: string;
  measurementTechnique: string;
  metrics?: Array<{
    name: string;
    value: string;
    unitText?: string;
  }>;
}

export interface StructuredDataProps {
  faqs?: FAQ[];
  services?: ServiceSchema[];
  caseStudy?: CaseStudySchema;
  enableOrganization?: boolean;
  enableWebsite?: boolean;
  enableBreadcrumbs?: boolean;
}

export function StructuredData({
  faqs,
  services,
  caseStudy,
  enableOrganization = true,
  enableWebsite = true,
  enableBreadcrumbs = true
}: StructuredDataProps) {
  const location = useLocation();

  // Organization Schema
  const organizationSchema = enableOrganization ? {
    "@context": "https://schema.org",
    "@type": ["Organization", "ProfessionalService"],
    "name": "Cosmos Wave",
    "alternateName": "Cosmos Wave - Agence Amazon SPN",
    "url": SITE_URL,
    "logo": absoluteUrl("/logo.png"),
    "description": "Agence Amazon partenaire SPN spécialisée dans la stratégie e-commerce Amazon, l'optimisation des fiches produits, Amazon Ads et la créativité de marque.",
    "foundingDate": "2021",
    "sameAs": [
      "https://www.linkedin.com/company/cosmoswave",
      "https://x.com/cosmos_wave_fr",
      "https://www.youtube.com/@cosmos-wave",
      "https://partnercentral.amazon.com/directory/search/details/cosmos-wave"
    ],
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "contactType": "sales",
        "email": "contact@cosmos-wave.com",
        "availableLanguage": ["fr", "en"],
        "areaServed": ["FR", "EU"]
      }
    ],
    "areaServed": [
      {
        "@type": "Country",
        "name": "France"
      },
      {
        "@type": "Place",
        "name": "Europe"
      }
    ],
    "knowsAbout": [
      "Amazon Ads",
      "Buy Box",
      "Brand Registry", 
      "A+ Content",
      "Amazon DSP",
      "FBA",
      "FBM",
      "Amazon SEO",
      "Amazon Store Design",
      "PPC Management",
      "Account Management"
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": 48.8566,
        "longitude": 2.3522
      },
      "geoRadius": "2000000"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Services Amazon",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Audit Amazon Gratuit",
            "description": "Audit complet de votre compte Amazon avec plan d'action priorisé"
          },
          "priceRange": "Gratuit"
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Service",
            "name": "Account Management Amazon",
            "description": "Gestion complète de votre compte Amazon par des experts SPN"
          },
          "priceRange": "€€€"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service", 
            "name": "Amazon Ads & DSP",
            "description": "Gestion campagnes publicitaires Amazon (SP/SB/SD/DSP)"
          },
          "priceRange": "€€"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "A+ Content & Amazon Store",
            "description": "Création contenus premium et design de Store Amazon"
          },
          "priceRange": "€€"
        }
      ]
    }
  } : null;

  // Website Schema with SearchAction
  const websiteSchema = enableWebsite ? {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Cosmos Wave",
    "url": SITE_URL,
    "description": "Agence Amazon partenaire SPN. Stratégie e-commerce Amazon, optimisation fiches produits, Amazon Ads et créativité de marque.",
    "publisher": {
      "@type": "Organization",
      "name": "Cosmos Wave",
      "url": SITE_URL
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${SITE_URL}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "mainEntity": {
      "@type": "Organization",
      "name": "Cosmos Wave"
    }
  } : null;

  // Breadcrumbs Schema
  const breadcrumbsSchema = enableBreadcrumbs ? generateBreadcrumbs(location.pathname) : null;

  // Services Schema
  const servicesSchema = services ? services.map(service => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.name,
    "description": service.description,
    "serviceType": service.serviceType,
    "provider": {
      "@type": "Organization",
      "name": "Cosmos Wave",
      "url": SITE_URL
    },
    "areaServed": service.areaServed.map(area => ({
      "@type": "Country",
      "name": area
    })),
    "offers": {
      "@type": "Offer",
      "priceRange": service.priceRange,
      "availability": "https://schema.org/InStock"
    }
  })) : null;

  // FAQ Schema
  const faqSchema = faqs ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  } : null;

  // Case Study Schema
  const caseStudySchema = caseStudy ? {
    "@context": "https://schema.org",
    "@type": ["Article", "CaseStudy"],
    "headline": caseStudy.name,
    "description": caseStudy.description,
    "datePublished": caseStudy.datePublished,
    "about": caseStudy.about,
    "author": {
      "@type": "Organization",
      "name": "Cosmos Wave",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization", 
      "name": "Cosmos Wave",
      "logo": {
        "@type": "ImageObject",
        "url": absoluteUrl("/logo.png")
      }
    },
    "measurementTechnique": caseStudy.measurementTechnique,
    "result": caseStudy.result,
    "additionalProperty": caseStudy.metrics?.map(metric => ({
      "@type": "PropertyValue",
      "name": metric.name,
      "value": metric.value,
      "unitText": metric.unitText
    })),
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": absoluteUrl(location.pathname)
    }
  } : null;

  return (
    <Helmet>
      {/* Organization + ProfessionalService Schema */}
      {organizationSchema && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}

      {/* Website + SearchAction Schema */}
      {websiteSchema && (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      )}

      {/* Breadcrumbs Schema */}
      {breadcrumbsSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbsSchema)}
        </script>
      )}

      {/* Services Schema */}
      {servicesSchema && servicesSchema.map((schema, index) => (
        <script key={`service-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* FAQ Schema */}
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}

      {/* Case Study Schema */}
      {caseStudySchema && (
        <script type="application/ld+json">
          {JSON.stringify(caseStudySchema)}
        </script>
      )}
    </Helmet>
  );
}

export default StructuredData;